import {
  AuthResponse,
  CurrentUser,
  LoginRequest,
  RegisterBuyerRequest,
  RegisterBuyerResult,
} from '../types';
import { USE_MOCK_AUTH } from '../constants/buyer-portal';
import { authMock } from '../mocks/authMock';
import { clinicHttp, setClinicToken, setRefreshToken } from './http';
import { ENDPOINTS } from './endpoints';

export type AuthService = {
  login: (req: LoginRequest) => Promise<AuthResponse>;
  register: (req: RegisterBuyerRequest) => Promise<RegisterBuyerResult>;
  me: (token: string) => Promise<CurrentUser>;
  logout: (refreshToken: string | null) => Promise<void>;
  refresh: (refreshToken: string) => Promise<AuthResponse>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, newPassword: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
};

const realAuthService: AuthService = {
  login: async (req) => {
    // Same email can now exist on both Buyer and Supplier sides — pin the
    // request to BUYER_USER so we never accidentally log into the wrong role.
    const res = await clinicHttp.post<AuthResponse>(ENDPOINTS.AUTH.LOGIN, {
      ...req,
      role: 'BUYER_USER',
    });
    return res.data;
  },
  register: async (req) => {
    // Backend expects flat shape: { email, password, businessName, fullName, phone,
    // createBusiness, defaultShippingAddress, defaultBillingAddress }.
    // Map frontend's nested `business` → flat `businessName` + lift addresses to top level.
    const payload = {
      email: req.email,
      password: req.password,
      fullName: req.fullName,
      phone: req.phone,
      createBusiness: req.createBusiness,
      businessName: req.business?.name,
      defaultShippingAddress:
        req.defaultShippingAddress ?? req.business?.shippingAddress,
      defaultBillingAddress:
        req.defaultBillingAddress ?? req.business?.billingAddress,
    };
    // Backend returns AuthResponse (auto-login). Persist tokens and synthesize a
    // RegisterBuyerResult so callers that expect the old shape keep working.
    const res = await clinicHttp.post<AuthResponse>(
      ENDPOINTS.AUTH.REGISTER_BUYER,
      payload,
    );
    const auth = res.data;
    setClinicToken(auth.accessToken);
    setRefreshToken(auth.refreshToken);
    return {
      userId: auth.user.id,
      createdBusinessId: auth.user.currentBusiness?.id,
      createdBusinessRole: auth.user.currentBusiness?.role,
      status: auth.user.status,
      emailVerificationRequired: false,
    };
  },
  me: async () => {
    const res = await clinicHttp.get<CurrentUser>(ENDPOINTS.AUTH.ME);
    return res.data;
  },
  logout: async (refreshToken) => {
    await clinicHttp.post(ENDPOINTS.AUTH.LOGOUT, { refreshToken });
  },
  refresh: async (refreshToken) => {
    const res = await clinicHttp.post<AuthResponse>(ENDPOINTS.AUTH.REFRESH, {
      refreshToken,
    });
    return res.data;
  },
  forgotPassword: async (email) => {
    // Same email can live on multiple portals — pin the role so backend hits
    // the buyer-side account and the reset link points back to the buyer
    // frontend.
    await clinicHttp.post(ENDPOINTS.AUTH.FORGOT_PASSWORD, {
      email,
      role: 'BUYER_USER',
    });
  },
  resetPassword: async (token, newPassword) => {
    await clinicHttp.post(ENDPOINTS.AUTH.RESET_PASSWORD, { token, newPassword });
  },
  changePassword: async (currentPassword, newPassword) => {
    await clinicHttp.post(ENDPOINTS.AUTH.CHANGE_PASSWORD, {
      currentPassword,
      newPassword,
    });
  },
};

// Adapter so authMock satisfies the new AuthService shape (mock doesn't need refresh/logout body).
const mockAdapter: AuthService = {
  login: authMock.login,
  register: authMock.register,
  me: authMock.me,
  logout: async () => authMock.logout(),
  refresh: async () => {
    throw new Error('Refresh not implemented in mock');
  },
  forgotPassword: async () => undefined,
  resetPassword: async () => undefined,
  changePassword: async () => undefined,
};

export const authService: AuthService = USE_MOCK_AUTH ? mockAdapter : realAuthService;
